/** Pure row-selection logic for Table (unit-tested in models.test.ts). */

export type HeaderCheckState = "none" | "some" | "all";

/** Header checkbox state for the given visible ids. */
export function headerState(selected: ReadonlySet<string>, ids: readonly string[]): HeaderCheckState {
  if (ids.length === 0) return "none";
  let hit = 0;
  for (const id of ids) if (selected.has(id)) hit++;
  if (hit === 0) return "none";
  return hit === ids.length ? "all" : "some";
}

/** Toggle one id. */
export function toggleId(selected: ReadonlySet<string>, id: string): Set<string> {
  const next = new Set(selected);
  if (next.has(id)) next.delete(id);
  else next.add(id);
  return next;
}

/**
 * Header checkbox click: if every visible id is selected, deselect them; otherwise select
 * them all. Ids selected outside the visible page are kept.
 */
export function toggleAll(selected: ReadonlySet<string>, ids: readonly string[]): Set<string> {
  const next = new Set(selected);
  if (headerState(selected, ids) === "all") for (const id of ids) next.delete(id);
  else for (const id of ids) next.add(id);
  return next;
}

/**
 * Shift-click range: set every id between `anchor` and `target` (inclusive, in `ids`
 * order) to `value`. Falls back to a single toggle if the anchor is not visible.
 */
export function selectRange(
  selected: ReadonlySet<string>,
  ids: readonly string[],
  anchor: string | null,
  target: string,
  value: boolean,
): Set<string> {
  const a = anchor === null ? -1 : ids.indexOf(anchor);
  const b = ids.indexOf(target);
  if (a < 0 || b < 0) return toggleId(selected, target);
  const [lo, hi] = a < b ? [a, b] : [b, a];
  const next = new Set(selected);
  for (let i = lo; i <= hi; i++) {
    if (value) next.add(ids[i]);
    else next.delete(ids[i]);
  }
  return next;
}
